import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import { PrioHealthStatus } from '../../../models/Zendesk';
import { apiFetchPrioHealthStatus } from '../api';
import { InformationBox } from '@prio365/prio365-react-library';
import { Button } from 'antd';
import PrioSpinner from '../../../components/PrioSpinner';
import { makePrioStyles } from '../../../theme/utils';
import { format, parseISO } from 'date-fns';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    justifyContent: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    height: '50%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  noStatus: {
    height: '50%',
    color: theme.colors.application.typography.default,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
}));
interface PrioStatusProps {
  className?: string;
}

export const PrioStatus: React.FC<PrioStatusProps> = (props) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { className } = props;
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [prioStatus, setPrioStatus] = React.useState<PrioHealthStatus[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const convertToInfoBoxType = (type: string): 'info' | 'warning' | 'error' => {
    switch (type) {
      case 'info':
        return 'info';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'critical':
        return 'error';
      default:
        return 'info';
    }
  };

  const handleUpdatePrioStatus = async () => {
    try {
      setLoading(true);
      const [result] = await Promise.all([
        apiFetchPrioHealthStatus(),
        new Promise((resolve) => setTimeout(resolve, 1000)), // Mindestwartezeit 1 Sekunde
      ]);

      if (result.data) {
        setPrioStatus(result.data);
      } else {
        setPrioStatus([]);
      }
    } finally {
      setLoading(false);
    }
  };

  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    handleUpdatePrioStatus();
  }, []);

  //#endregion

  return (
    <div className={classNames(classes.root, className)}>
      {loading && (
        <div className={classes.loadingContainer}>
          <PrioSpinner size="large" />
        </div>
      )}
      {prioStatus.length === 0 && !loading && (
        <div className={classes.noStatus}>
          {t('zendesk:drawer.prioStatus.noStatus')}
        </div>
      )}

      {!loading &&
        prioStatus.map((status) => (
          <InformationBox
            key={status.statusMessageId}
            description={status.description}
            title={status.context}
            extra={format(parseISO(status.timestamp), 'dd.MM.yyyy - HH:mm')}
            type={convertToInfoBoxType(status.errorLevel)}
          ></InformationBox>
        ))}
      {!loading && (
        <Button
          type="link"
          onClick={handleUpdatePrioStatus}
          style={{ marginTop: theme.old.spacing.unit(2) }}
        >
          {t('zendesk:drawer.prioStatus.refreshStatus')}
        </Button>
      )}
    </div>
  );
};

export default PrioStatus;
